/**
 * Button Component
 *
 * A versatile button component built with Community Trust design tokens.
 * Supports multiple variants, sizes, and states.
 */

import React from 'react';
import { clsx } from 'clsx';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Visual style variant
   */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';

  /**
   * Size of the button
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Whether the button should take full width
   */
  fullWidth?: boolean;

  /**
   * Loading state (shows spinner, disables interaction)
   */
  loading?: boolean;

  /**
   * Icon to display before text
   */
  leftIcon?: React.ReactNode;

  /**
   * Icon to display after text
   */
  rightIcon?: React.ReactNode;

  /**
   * Button content
   */
  children: React.ReactNode;
}

/**
 * Button component using Community Trust design system
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      leftIcon,
      rightIcon,
      disabled,
      className,
      children,
      ...props
    },
    ref
  ) => {
    // Base styles (always applied)
    const baseStyles = clsx(
      'inline-flex items-center justify-center',
      'font-medium transition-all',
      'focus:outline-none focus:ring-2 focus:ring-offset-2',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      'rounded-lg', // 8px border radius from design tokens
      {
        'w-full': fullWidth,
        'cursor-wait': loading,
      }
    );

    // Variant styles
    const variantStyles = {
      primary: clsx(
        'bg-brand-primary text-white',
        'hover:bg-brand-primary-dark',
        'focus:ring-brand-primary',
        'shadow-sm'
      ),
      secondary: clsx(
        'bg-brand-secondary text-white',
        'hover:bg-brand-secondary-dark',
        'focus:ring-brand-secondary',
        'shadow-sm'
      ),
      outline: clsx(
        'border-2 border-brand-primary text-brand-primary bg-transparent',
        'hover:bg-brand-primary hover:text-white',
        'focus:ring-brand-primary'
      ),
      ghost: clsx(
        'text-brand-primary bg-transparent',
        'hover:bg-brand-primary-light/10',
        'focus:ring-brand-primary'
      ),
      danger: clsx(
        'bg-error-500 text-white',
        'hover:bg-error-600',
        'focus:ring-error-500',
        'shadow-sm'
      ),
    };

    // Size styles (using design token spacing)
    const sizeStyles = {
      sm: 'px-3 py-1.5 text-sm', // px-3 = 12px, py-1.5 = 6px
      md: 'px-4 py-2 text-base',  // px-4 = 16px, py-2 = 8px (base)
      lg: 'px-6 py-3 text-lg',    // px-6 = 24px, py-3 = 12px
    };

    // Icon spacing
    const iconSpacing = {
      sm: 'space-x-1.5', // 6px
      md: 'space-x-2',   // 8px
      lg: 'space-x-2.5', // 10px
    };

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={clsx(
          baseStyles,
          variantStyles[variant],
          sizeStyles[size],
          (leftIcon || rightIcon) && iconSpacing[size],
          className
        )}
        {...props}
      >
        {loading && (
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}

        {!loading && leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}

        <span>{children}</span>

        {!loading && rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
